import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Brain, CheckSquare, History, Sparkles } from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import { useAuth } from '../lib/auth'

type AgentRow = {
  agent_id: string
  name: string
  project_id: string
  status: string
}

type SkillRow = {
  skill_id: string
  name: string
  description?: string
  agent_id: string
  project_id: string
  security?: {
    scope?: string
    global?: boolean
    home_project_id?: string
    allowed_project_ids?: string[]
    checkboxes?: {
      use_in_home_project?: boolean
      use_across_selected_projects?: boolean
      use_globally?: boolean
    }
  }
  learning?: {
    intelligence?: {
      summary?: string
      confidence?: number
      task_count?: number
      success_count?: number
      patterns?: string[]
    }
    history?: Array<{
      history_id: string
      task: string
      outcome: string
      success: boolean
      lessons?: string
      recorded_at: string
    }>
  }
}

type ProjectOption = { project_id: string; name: string }

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json() as Promise<T>
}

export function SkillsPage() {
  const { user, loading } = useAuth()
  const customerId = user?.customer_id
  const projectId = user?.project_id
  const [agents, setAgents] = useState<AgentRow[]>([])
  const [projects, setProjects] = useState<ProjectOption[]>([])
  const [selectedAgent, setSelectedAgent] = useState('')
  const [skills, setSkills] = useState<SkillRow[]>([])
  const [intel, setIntel] = useState<unknown[]>([])
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [crossProject, setCrossProject] = useState(false)
  const [globalUse, setGlobalUse] = useState(false)
  const [allowed, setAllowed] = useState<string[]>([])
  const [task, setTask] = useState('')
  const [outcome, setOutcome] = useState('')
  const [lessons, setLessons] = useState('')
  const [historySkill, setHistorySkill] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const otherProjects = useMemo(
    () => projects.filter((p) => p.project_id !== projectId),
    [projects, projectId],
  )

  async function refresh() {
    if (!customerId) return
    const agentRows = await api<AgentRow[]>(
      `/api/afos360/v1/customers/${encodeURIComponent(customerId)}/agents`,
    )
    setAgents(agentRows)
    if (!selectedAgent && agentRows[0]) setSelectedAgent(agentRows[0].agent_id)
    const aid = selectedAgent || agentRows[0]?.agent_id
    if (aid) {
      const skillRows = await api<SkillRow[]>(
        `/api/afos360/v1/customers/${encodeURIComponent(customerId)}/agents/${encodeURIComponent(aid)}/skills`,
      )
      setSkills(skillRows)
      if (!historySkill && skillRows[0]) setHistorySkill(skillRows[0].skill_id)
    } else {
      setSkills([])
    }
    if (projectId) {
      const usable = await api<{ intelligence: unknown[] }>(
        `/api/afos360/v1/customers/${encodeURIComponent(customerId)}/projects/${encodeURIComponent(projectId)}/usable-skills`,
      )
      setIntel(usable.intelligence || [])
    }
    try {
      const rows = await api<ProjectOption[]>(
        `/api/afos360/tenants/${encodeURIComponent(customerId)}/projects`,
      )
      setProjects(rows.length ? rows : projectId ? [{ project_id: projectId, name: 'Current project' }] : [])
    } catch {
      if (projectId) setProjects([{ project_id: projectId, name: 'Current project' }])
    }
  }

  useEffect(() => {
    if (user) void refresh().catch((err) => setError(err instanceof Error ? err.message : 'Load failed'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedAgent])

  if (!loading && !user) return <Navigate to="/signin" replace />

  async function onCreateSkill(e: FormEvent) {
    e.preventDefault()
    if (!customerId || !selectedAgent) return
    setError(null)
    setMessage(null)
    try {
      const scope = globalUse ? 'global' : crossProject ? 'cross_project' : 'project'
      const skill = await api<SkillRow>(
        `/api/afos360/v1/customers/${encodeURIComponent(customerId)}/agents/${encodeURIComponent(selectedAgent)}/skills`,
        {
          method: 'POST',
          body: JSON.stringify({
            name,
            description,
            scope,
            global_use: globalUse,
            allowed_project_ids: crossProject ? allowed : [],
          }),
        },
      )
      setMessage(`Skill ${skill.skill_id} created`)
      setName('')
      setDescription('')
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed')
    }
  }

  async function onSaveSecurity(skill: SkillRow) {
    if (!customerId) return
    setError(null)
    try {
      const boxes = skill.security?.checkboxes
      await api(
        `/api/afos360/v1/customers/${encodeURIComponent(customerId)}/agents/${encodeURIComponent(skill.agent_id)}/skills/${encodeURIComponent(skill.skill_id)}/security`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            scope: boxes?.use_globally
              ? 'global'
              : boxes?.use_across_selected_projects
                ? 'cross_project'
                : 'project',
            global_use: !!boxes?.use_globally,
            allowed_project_ids: skill.security?.allowed_project_ids || [],
          }),
        },
      )
      setMessage(`Updated security for ${skill.skill_id}`)
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Security update failed')
    }
  }

  async function onLearn(e: FormEvent) {
    e.preventDefault()
    if (!customerId || !selectedAgent || !historySkill) return
    setError(null)
    try {
      await api(
        `/api/afos360/v1/customers/${encodeURIComponent(customerId)}/agents/${encodeURIComponent(selectedAgent)}/skills/${encodeURIComponent(historySkill)}/history`,
        {
          method: 'POST',
          body: JSON.stringify({ task, outcome, lessons, success: true }),
        },
      )
      setMessage('Learning history recorded')
      setTask('')
      setOutcome('')
      setLessons('')
      await refresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Learn failed')
    }
  }

  function toggleAllowed(pid: string) {
    setAllowed((prev) => (prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]))
  }

  return (
    <AppChrome>
      <main className="afos-page grid gap-6 lg:grid-cols-2">
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2"
        >
          <h1 className="afos-page-title">Agent skills</h1>
          <p className="afos-page-lead">
            Every skill gets a <code className="text-[var(--color-cyan)]">SKL_</code> ULID.
            Share across projects with checkboxes, or enable global use. Intelligence learning
            and history feed future tasks.
          </p>
          {message && (
            <p className="mt-3 text-sm text-[var(--color-cyan)]">{message}</p>
          )}
          {error && <p className="mt-3 text-sm text-[var(--color-rose)]">{error}</p>}
        </motion.section>

        <section className="space-y-4 rounded-3xl border border-[var(--color-line)] bg-black/25 p-6">
          <h2 className="flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
            <Sparkles className="h-5 w-5 text-[var(--color-amber)]" />
            Create skill
          </h2>
          <label className="block text-xs uppercase tracking-wider text-[var(--color-mist)]">
            Agent
            <select
              className="mt-1 w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              value={selectedAgent}
              onChange={(e) => setSelectedAgent(e.target.value)}
            >
              {agents.map((a) => (
                <option key={a.agent_id} value={a.agent_id}>
                  {a.name} · {a.agent_id}
                </option>
              ))}
            </select>
          </label>
          <form className="space-y-3" onSubmit={onCreateSkill}>
            <input
              className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              placeholder="Skill name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <textarea
              className="min-h-24 w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              placeholder="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <div className="space-y-2 rounded-2xl border border-[var(--color-line)] bg-black/20 p-4">
              <div className="mb-1 flex items-center gap-2 text-sm font-semibold">
                <CheckSquare className="h-4 w-4 text-[var(--color-cyan)]" />
                Security sharing
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked disabled />
                Use in home project
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={crossProject}
                  onChange={(e) => setCrossProject(e.target.checked)}
                />
                Use across selected projects
              </label>
              {crossProject && (
                <div className="ml-6 space-y-1">
                  {otherProjects.length === 0 && (
                    <p className="text-xs text-[var(--color-mist)]">No other projects yet</p>
                  )}
                  {otherProjects.map((p) => (
                    <label key={p.project_id} className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        checked={allowed.includes(p.project_id)}
                        onChange={() => toggleAllowed(p.project_id)}
                      />
                      {p.name} · {p.project_id}
                    </label>
                  ))}
                </div>
              )}
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={globalUse}
                  onChange={(e) => setGlobalUse(e.target.checked)}
                />
                Use globally
              </label>
            </div>
            <button
              type="submit"
              className="w-full rounded-full bg-[var(--color-foam)] px-4 py-3 font-semibold text-[var(--color-void)]"
              disabled={!selectedAgent}
            >
              Mint SKL_ skill
            </button>
          </form>
        </section>

        <section className="space-y-4 rounded-3xl border border-[var(--color-line)] bg-black/25 p-6">
          <h2 className="flex items-center gap-2 font-[family-name:var(--font-display)] text-xl font-bold">
            <History className="h-5 w-5 text-[var(--color-cyan)]" />
            Record learning history
          </h2>
          <form className="space-y-3" onSubmit={onLearn}>
            <select
              className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              value={historySkill}
              onChange={(e) => setHistorySkill(e.target.value)}
            >
              {skills.map((s) => (
                <option key={s.skill_id} value={s.skill_id}>
                  {s.name} · {s.skill_id}
                </option>
              ))}
            </select>
            <input
              className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              placeholder="Task"
              value={task}
              onChange={(e) => setTask(e.target.value)}
              required
            />
            <input
              className="w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              placeholder="Outcome"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value)}
              required
            />
            <textarea
              className="min-h-20 w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3"
              placeholder="Lessons for future tasks"
              value={lessons}
              onChange={(e) => setLessons(e.target.value)}
            />
            <button
              type="submit"
              className="w-full rounded-full border border-[var(--color-line)] px-4 py-3 font-semibold"
              disabled={!historySkill}
            >
              Append history + refresh intelligence
            </button>
          </form>
        </section>

        <section className="space-y-3 rounded-3xl border border-[var(--color-line)] bg-black/25 p-6 lg:col-span-2">
          <h2 className="font-[family-name:var(--font-display)] text-xl font-bold">
            Skills for this agent
          </h2>
          <div className="grid gap-3 md:grid-cols-2">
            {skills.map((skill) => (
              <div
                key={skill.skill_id}
                className="rounded-2xl border border-[var(--color-line)] bg-black/20 p-4"
              >
                <div className="font-semibold">{skill.name}</div>
                <div className="font-[family-name:var(--font-mono)] text-[11px] text-[var(--color-mist)]">
                  {skill.skill_id}
                </div>
                <p className="mt-2 text-sm text-[var(--color-mist)]">{skill.description}</p>
                <div className="mt-3 space-y-1 text-xs">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!skill.security?.checkboxes?.use_across_selected_projects}
                      onChange={(e) => {
                        const next = {
                          ...skill,
                          security: {
                            ...skill.security,
                            checkboxes: {
                              ...skill.security?.checkboxes,
                              use_across_selected_projects: e.target.checked,
                              use_globally: e.target.checked
                                ? false
                                : skill.security?.checkboxes?.use_globally,
                            },
                          },
                        }
                        setSkills((prev) =>
                          prev.map((s) => (s.skill_id === skill.skill_id ? next : s)),
                        )
                      }}
                    />
                    Cross-project
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={!!skill.security?.checkboxes?.use_globally || !!skill.security?.global}
                      onChange={(e) => {
                        const next = {
                          ...skill,
                          security: {
                            ...skill.security,
                            checkboxes: {
                              ...skill.security?.checkboxes,
                              use_globally: e.target.checked,
                              use_across_selected_projects: e.target.checked
                                ? false
                                : skill.security?.checkboxes?.use_across_selected_projects,
                            },
                          },
                        }
                        setSkills((prev) =>
                          prev.map((s) => (s.skill_id === skill.skill_id ? next : s)),
                        )
                      }}
                    />
                    Global
                  </label>
                  <button
                    type="button"
                    className="mt-2 text-[var(--color-cyan)] underline"
                    onClick={() => void onSaveSecurity(skill)}
                  >
                    Save security
                  </button>
                </div>
                {skill.learning?.intelligence?.summary && (
                  <p className="mt-3 text-xs text-[var(--color-mist)]">
                    <Brain className="mr-1 inline h-3 w-3" />
                    {skill.learning.intelligence.summary}
                  </p>
                )}
              </div>
            ))}
            {skills.length === 0 && (
              <p className="text-sm text-[var(--color-mist)]">No skills yet for this agent.</p>
            )}
          </div>
        </section>

        <section className="rounded-3xl border border-[var(--color-line)] bg-black/25 p-6 lg:col-span-2">
          <h2 className="mb-3 font-[family-name:var(--font-display)] text-xl font-bold">
            Intelligence for future tasks
          </h2>
          <pre className="afos-scroll-x overflow-auto rounded-2xl bg-black/40 p-4 text-xs text-[var(--color-mist)]">
            {JSON.stringify(intel, null, 2)}
          </pre>
        </section>
      </main>
    </AppChrome>
  )
}
